import { useCallback, useEffect, useRef, useState } from 'react';
import type { FishingResultDto, FishingStartDto } from '@bb/shared';
import { api, type ProgressNews } from '../net/api';
import { drawFish } from '../art/items';
import { play } from '../audio/sound';
import { Sheet } from './Sheet';

type Phase =
  | { kind: 'ready' }
  | { kind: 'casting' }
  | { kind: 'waiting'; s: FishingStartDto; startedAt: number }
  | { kind: 'bite'; s: FishingStartDto; startedAt: number; biteAt: number }
  | { kind: 'reeling'; s: FishingStartDto; startedAt: number; hits: number; misses: number }
  | { kind: 'landing' }
  | { kind: 'result'; r: FishingResultDto }
  | { kind: 'error'; message: string };

const W = 480;
const H = 260;
/** How long the bobber stays down before the fish lets go. */
const BITE_WINDOW_MS = 1600;
const HITS_NEEDED = 3;
const MISSES_ALLOWED = 3;
/** Matches the server's minimum reel time, with a little margin. */
const MIN_REEL_MS = 1400;
const ZONE = { from: 0.4, to: 0.6 };

export function Fishing({ onClose, onChanged }: { onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [phase, setPhase] = useState<Phase>({ kind: 'ready' });
  const canvas = useRef<HTMLCanvasElement>(null);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const needle = useRef(0);
  /** Guards against reporting one session twice (animation frames can race React renders). */
  const reported = useRef(false);
  const go = (next: Phase) => {
    phaseRef.current = next;
    setPhase(next);
  };

  const finish = useCallback(
    async (s: FishingStartDto, startedAt: number, outcome: 'caught' | 'escaped') => {
      if (reported.current) return;
      reported.current = true;
      go({ kind: 'landing' });
      // Never report faster than the server will accept.
      const wait = s.biteAfterMs + MIN_REEL_MS - (performance.now() - startedAt);
      if (outcome === 'caught' && wait > 0) await new Promise((r) => setTimeout(r, wait));
      try {
        const r = await api.finishFishing(s.sessionId, outcome);
        if (r.caught) play('splash');
        go({ kind: 'result', r });
        onChanged((r as { progress?: ProgressNews }).progress);
      } catch (e) {
        go({ kind: 'error', message: (e as Error).message });
      }
    },
    [onChanged],
  );

  async function cast() {
    reported.current = false;
    go({ kind: 'casting' });
    try {
      const s = await api.startFishing();
      go({ kind: 'waiting', s, startedAt: performance.now() });
    } catch (e) {
      go({ kind: 'error', message: (e as Error).message });
    }
  }

  const act = useCallback(() => {
    const p = phaseRef.current;
    if (p.kind === 'bite') {
      go({ kind: 'reeling', s: p.s, startedAt: p.startedAt, hits: 0, misses: 0 });
    } else if (p.kind === 'waiting') {
      // Too early: the fish is spooked.
      void finish(p.s, p.startedAt, 'escaped');
    } else if (p.kind === 'reeling') {
      const n = needle.current;
      const hit = n >= ZONE.from && n <= ZONE.to;
      const hits = p.hits + (hit ? 1 : 0);
      const misses = p.misses + (hit ? 0 : 1);
      if (hits >= HITS_NEEDED) void finish(p.s, p.startedAt, 'caught');
      else if (misses >= MISSES_ALLOWED) void finish(p.s, p.startedAt, 'escaped');
      else go({ ...p, hits, misses });
    }
  }, [finish]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        e.preventDefault();
        act();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [act]);

  // Animation loop: water, bobber, reel bar.
  useEffect(() => {
    let raf = 0;
    const c = canvas.current?.getContext('2d');
    if (!c) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.current!.width = W * dpr;
    canvas.current!.height = H * dpr;
    c.setTransform(dpr, 0, 0, dpr, 0, 0);
    const draw = () => {
      const p = phaseRef.current;
      const t = performance.now();
      if (p.kind === 'waiting' && t - p.startedAt >= p.s.biteAfterMs) go({ ...p, kind: 'bite', biteAt: t });
      if (p.kind === 'bite' && t - p.biteAt > BITE_WINDOW_MS) void finish(p.s, p.startedAt, 'escaped');

      c.fillStyle = '#2f93a8';
      c.fillRect(0, 0, W, H);
      c.fillStyle = '#4fb3c4';
      c.fillRect(0, 70, W, H - 70);
      c.fillStyle = 'rgba(255,255,255,0.35)';
      for (let i = 0; i < 12; i++) {
        const x = ((i * 97 + t / 30) % (W + 40)) - 20;
        c.fillRect(x, 90 + ((i * 41) % 150), 22, 2);
      }
      // Line and bobber.
      const inWater = p.kind === 'waiting' || p.kind === 'bite' || p.kind === 'reeling';
      if (inWater) {
        const dip = p.kind === 'bite' ? 10 + Math.sin(t / 60) * 4 : Math.sin(t / 400) * 3;
        c.strokeStyle = 'rgba(255,255,255,0.8)';
        c.lineWidth = 1.5;
        c.beginPath();
        c.moveTo(W - 20, 0);
        c.quadraticCurveTo(W - 120, 40, W / 2, 110 + dip);
        c.stroke();
        c.fillStyle = '#d4553e';
        c.beginPath();
        c.arc(W / 2, 110 + dip, 9, Math.PI, 0);
        c.fill();
        c.fillStyle = '#ffffff';
        c.beginPath();
        c.arc(W / 2, 110 + dip, 9, 0, Math.PI);
        c.fill();
        if (p.kind === 'bite') {
          c.strokeStyle = 'rgba(255,255,255,0.7)';
          c.lineWidth = 2;
          for (const r of [16, 26]) {
            c.beginPath();
            c.ellipse(W / 2, 120, r + ((t / 20) % 10), (r + ((t / 20) % 10)) / 3, 0, 0, Math.PI * 2);
            c.stroke();
          }
        }
      }
      if (p.kind === 'reeling') {
        const speed = 0.0011 * p.s.difficulty;
        needle.current = (Math.sin(t * speed) + 1) / 2;
        const bx = 40;
        const bw = W - 80;
        c.fillStyle = 'rgba(18,40,58,0.75)';
        c.beginPath();
        c.roundRect(bx - 10, 190, bw + 20, 50, 12);
        c.fill();
        c.fillStyle = '#e8708f';
        c.fillRect(bx, 205, bw, 20);
        c.fillStyle = '#6fcf93';
        c.fillRect(bx + bw * ZONE.from, 205, bw * (ZONE.to - ZONE.from), 20);
        c.fillStyle = '#ffffff';
        c.fillRect(bx + bw * needle.current - 2, 198, 4, 34);
        for (let i = 0; i < HITS_NEEDED; i++) {
          c.fillStyle = i < p.hits ? '#e9a915' : 'rgba(255,255,255,0.35)';
          c.beginPath();
          c.arc(W / 2 - 24 + i * 24, 175, 7, 0, Math.PI * 2);
          c.fill();
        }
      }
      if (p.kind === 'result' && p.r.caught) {
        drawFish(c, p.r.caught.color, 2.2, W / 2, 140 + Math.sin(t / 200) * 4);
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, [finish]);

  const prompt = (() => {
    switch (phase.kind) {
      case 'ready':
        return 'Cast your line and wait for the bobber to dip.';
      case 'casting':
        return 'Casting…';
      case 'waiting':
        return 'Wait for it… (clicking now scares the fish)';
      case 'bite':
        return 'A bite! Click or press Space now!';
      case 'reeling':
        return `Reel in: hit the green zone ${HITS_NEEDED} times (${phase.misses}/${MISSES_ALLOWED} misses).`;
      case 'landing':
        return 'Landing it…';
      case 'result':
        return phase.r.caught ? '' : 'It got away. Try again!';
      case 'error':
        return phase.message;
    }
  })();

  return (
    <Sheet title="Pier Fishing" subtitle="Pebble Beach" onClose={onClose}>
      <div className="fishing">
        <canvas
          ref={canvas}
          className="fishing-canvas"
          style={{ width: W, height: H }}
          onPointerDown={act}
          aria-label="Fishing game. Press Space or click to act."
          role="img"
        />
        {phase.kind === 'result' && phase.r.caught && (
          <div className={`catch rarity-${phase.r.caught.rarity}`}>
            <strong>You caught a {phase.r.caught.name}!</strong>
            <span className="tag">{phase.r.caught.rarity}</span>
            <p className="muted small-print">{phase.r.caught.description}</p>
            <p>
              +{phase.r.coins} coins · +{phase.r.xp} XP
              {phase.r.leveledUp && <strong> · Level up! You're now level {phase.r.level}.</strong>}
            </p>
            {phase.r.dailyCapReached && <p className="muted small-print">You've reached today's coin limit for fishing. Fish and XP still count.</p>}
          </div>
        )}
        <p className={phase.kind === 'bite' ? 'prompt hot' : phase.kind === 'error' ? 'form-error' : 'prompt'} role="status">{prompt}</p>
        {(phase.kind === 'ready' || phase.kind === 'result' || phase.kind === 'error') && (
          <button className="btn" onClick={() => void cast()}>{phase.kind === 'ready' ? 'Cast line' : 'Cast again'}</button>
        )}
      </div>
    </Sheet>
  );
}
